import { ThunkAction, ThunkDispatch } from "redux-thunk";
import {
    getCurrentTemplate,
    getPaneState, getPanning,
    getSlides,
    getZoomLevel,
    HelpPanelContentType,
    SketchMode,
    State
} from "../reducers";
import { Locale } from "../services/translation-store";
import { EPSILON, MIN_ZOOM_LEVEL, SLIDE_HEIGHT, SLIDE_WIDTH } from "../constants";


export interface ReportErrorAction {
    readonly type: 'REPORT_ERROR',
    readonly error: Error,
}

export interface SetLocaleAction {
    readonly type: 'SET_LOCALE',
    readonly locale: Locale,
}

export interface SetSketchModeAction {
    readonly type: 'SET_SKETCH_MODE';
    readonly sketchMode: SketchMode;
}

export interface SetZoomLevelAction {
    readonly type: 'SET_ZOOM_LEVEL',
    readonly zoomSliderValue: number,
    readonly scrollLeft: number,
    readonly scrollTop: number,
}

export interface SetScrollAction {
    readonly type: 'SET_SCROLL',
    readonly scrollLeft: number,
    readonly scrollTop: number,
}

export interface SetPaneSizeAction {
    readonly type: 'SET_PANE_SIZE',
    readonly width: number,
    readonly height: number,
}

export interface StartPanningAction {
    readonly type: 'START_PANNING',
    readonly offsetX: number,
    readonly offsetY: number,
}

export interface PanningAction {
    readonly type: 'PANNING',
    readonly newScrollLeft: number,
    readonly newScrollTop: number,
}

export interface StopPanningAction {
    readonly type: 'STOP_PANNING',
    readonly newScrollLeft: number,
    readonly newScrollTop: number,
}

export interface ShowHelpAction {
    readonly type: 'SHOW_HELP',
}

export interface HideHelpAction {
    readonly type: 'HIDE_HELP',
}

export interface ShowHelpContentAction {
    readonly type: 'SHOW_HELP_CONTENT',
    readonly contentType: HelpPanelContentType,
    readonly content: string,
    readonly backlink: string;
}

export interface StartHelpPanelDraggingAction {
    readonly type: 'START_HELP_PANEL_DRAGGING',
}

export interface HelpPanelDraggingAction {
    readonly type: 'HELP_PANEL_DRAGGING',
    readonly width: number,
}

export interface StopHelpPanelDraggingAction {
    readonly type: 'STOP_HELP_PANEL_DRAGGING',
    readonly width: number,
}


export function reportError(error: Error): ReportErrorAction {
    return {
        type: 'REPORT_ERROR',
        error
    };
}

export function setLocale(locale: Locale): SetLocaleAction {
    return {
        type: 'SET_LOCALE',
        locale,
    };
}

export function setSketchMode(sketchMode: SketchMode): SetSketchModeAction {
    return {
        type: 'SET_SKETCH_MODE',
        sketchMode,
    };
}

export interface ShowGenericDialogAction {
    readonly type: 'SHOW_GENERIC_DIALOG',
    readonly title: string,
    readonly text: string,
}

export interface HideGenericDialogAction {
    readonly type: 'HIDE_GENERIC_DIALOG',
}

export function setZoomLevel(newZoomSliderValue: number): ThunkAction<any, State, any, SetZoomLevelAction> {
    return async(dispatch: ThunkDispatch<State, any, SetZoomLevelAction>, getState: () => State): Promise<any> => {
        const state = getState();
        const { maxZoomLevel, scrollLeft, scrollTop, paneWidth, paneHeight } = getPaneState(state);
        const oldZoomLevel = getZoomLevel(state);
        const newZoomLevel = newZoomSliderValue * newZoomSliderValue * (maxZoomLevel - MIN_ZOOM_LEVEL) + MIN_ZOOM_LEVEL;
        const newScrollLeft = Math.max(0, Math.round((scrollLeft + 0.5 * paneWidth) / oldZoomLevel * newZoomLevel - 0.5 * paneWidth));
        const newScrollTop = Math.max(0, Math.round((scrollTop + 0.5 * paneHeight) / oldZoomLevel * newZoomLevel - 0.5 * paneHeight));
        dispatch({
            type: 'SET_ZOOM_LEVEL',
            zoomSliderValue: newZoomSliderValue,
            scrollLeft: newScrollLeft,
            scrollTop: newScrollTop,
        });
    };
}

export function setScroll(newScrollLeft: number, newScrollTop: number): ThunkAction<any, State, any, SetScrollAction> {
    return async (dispatch: ThunkDispatch<State, any, SetScrollAction>, getState: () => State): Promise<any> => {
        const state = getState();
        const { scrollLeft, scrollTop } = getPaneState(state);
        if (scrollLeft !== newScrollLeft || scrollTop !== newScrollTop) {
            dispatch({
                type: 'SET_SCROLL',
                scrollLeft: newScrollLeft,
                scrollTop: newScrollTop,
            });
        }
    };
}


export function zoomInSlide(uuid: string): ThunkAction<any, State, any, SetZoomLevelAction | SetScrollAction> {
    return async (dispatch: ThunkDispatch<State, any, SetZoomLevelAction | SetScrollAction>, getState: () => State): Promise<any> => {
        const state = getState();

        const { zoomSliderValue, maxZoomLevel, paneWidth, paneHeight } = getPaneState(state);
        if (Math.abs(zoomSliderValue - 1) < EPSILON) {
            dispatch(setZoomLevel(0.0));
        } else {

            const slides = getSlides(state);
            const slide = slides.find(currentSlide => currentSlide.uuid === uuid);

            if (slide) {
                const {storyboardWidth: width, storyboardHeight: height} = getCurrentTemplate(state);
                const canvasRatio = width / height;

                const gapLeft = Math.max(0, maxZoomLevel * (paneWidth - canvasRatio * paneHeight) / 2);
                const pixelWidth = maxZoomLevel * paneWidth - 2 * gapLeft;
                const posX = (slide.x + SLIDE_WIDTH / 2) * pixelWidth / width;
                const scrollLeft = posX - paneWidth / 2 + gapLeft;

                const gapTop = Math.max(0, maxZoomLevel * (paneHeight - paneWidth / canvasRatio) / 2);
                const pixelHeight = maxZoomLevel * paneHeight - 2 * gapTop;
                const posY = (slide.y + SLIDE_HEIGHT / 2) * pixelHeight / height;
                const scrollTop = posY - paneHeight / 2 + gapTop;

                dispatch({
                    type: 'SET_ZOOM_LEVEL',
                    zoomSliderValue: 1.0,
                    scrollLeft: 0,
                    scrollTop: 0,
                });
                setTimeout(() => {
                    dispatch({
                        type: 'SET_SCROLL',
                        scrollLeft,
                        scrollTop,
                    });
                });
            }
        }
    }
}

export function setPaneSize(width: number, height: number): SetPaneSizeAction {
    return {
        type: 'SET_PANE_SIZE',
        width,
        height,
    }
}

export function startPanning(mouseX: number, mouseY: number): ThunkAction<any, State, any, StartPanningAction> {
    return async (dispatch: ThunkDispatch<State, any, StartPanningAction>, getState: () => State): Promise<any> => {
        const state = getState();
        const { scrollLeft, scrollTop } = getPaneState(state);
        const offsetX = scrollLeft + mouseX;
        const offsetY = scrollTop + mouseY;
        dispatch({
            type: 'START_PANNING',
            offsetX,
            offsetY,
        });
    };
}

export function panning(mouseX: number, mouseY: number): ThunkAction<any, State, any, PanningAction> {
    return async (dispatch: ThunkDispatch<State, any, PanningAction>, getState: () => State): Promise<any> => {
        const state = getState();
        const panningState = getPanning(state);
        if (panningState) {
            const {offsetX, offsetY} = panningState;
            const newScrollLeft = offsetX - mouseX;
            const newScrollTop = offsetY - mouseY;
            const {scrollLeft, scrollTop} = getPaneState(state);
            if (scrollLeft !== newScrollLeft || scrollTop !== newScrollTop) {
                dispatch({
                    type: 'PANNING',
                    newScrollLeft,
                    newScrollTop,
                });
            }
        }
    };
}

export function stopPanning(mouseX: number, mouseY: number): ThunkAction<any, State, any, StopPanningAction> {
    return async (dispatch: ThunkDispatch<State, any, StopPanningAction>, getState: () => State): Promise<any> => {
        const state = getState();
        const panningState = getPanning(state);
        if (panningState) {
            const {offsetX, offsetY} = panningState;
            const newScrollLeft = offsetX - mouseX;
            const newScrollTop = offsetY - mouseY;
            dispatch({
                type: 'STOP_PANNING',
                newScrollLeft,
                newScrollTop,
            });
        }
    };
}


export function showHelp(): ShowHelpAction {
    return {
        type: 'SHOW_HELP',
    };
}

export function hideHelp(): HideHelpAction {
    return {
        type: 'HIDE_HELP',
    };
}

export function showHelpContent(contentType: HelpPanelContentType, content: string, backlink: string = ''): ShowHelpContentAction {
    return {
        type: 'SHOW_HELP_CONTENT',
        contentType,
        content,
        backlink,
    };
}

export function showGenericDialog(title: string, text: string): ShowGenericDialogAction {
    return {
        type: 'SHOW_GENERIC_DIALOG',
        title,
        text,
    }
}

export function hideGenericDialog(): HideGenericDialogAction {
    return {
        type: 'HIDE_GENERIC_DIALOG',
    }
}

export function startHelpPanelDragging(): StartHelpPanelDraggingAction {
    return {
        type: 'START_HELP_PANEL_DRAGGING',
    }
}

export function helpPanelDragging(width: number): HelpPanelDraggingAction {
    return {
        type: 'HELP_PANEL_DRAGGING',
        width,
    }
}

export function stopHelpPanelDragging(width: number): StopHelpPanelDraggingAction {
    return {
        type: 'STOP_HELP_PANEL_DRAGGING',
        width,
    }
}


// WEBPACK FOOTER //
// ./src/actions/ui-state.ts